import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { TooltipRenderer, Text, Box, IconPositive, Notice, HiddenVisually, ButtonIcon, IconBookmark, Button, ButtonLink, useResponsiveValue, Hidden, Link, Stack, Columns, Column, Badge, Heading, TextLink, Inline, List, Bleed } from "braid-design-system";
import { translations as translations$2 } from "../.vocab/index.mjs";
import { splitView, root, root_isStandOut, root_isPremium, tappableJob, outlinedJob, trackingPixel, showVisited, showHovered } from "./JobCard.css.mjs";
import { LazyLoadedImg } from "./components/LazyLoadedImg/LazyLoadedImg.mjs";
import { MetaLink } from "./components/MetaLink/MetaLink.mjs";
import { memo } from "react";
import { translations } from "../components/SaveJobForSignedIn/.vocab/index.mjs";
import { translations as translations$1 } from "../components/SaveJobForSignedOut/.vocab/index.mjs";
import { getJobDataAutomation, generateJobTitleLinkId, highlightFromHTML } from "./utils/helpers.mjs";
const SavedJobForApplied = ({
  jobId,
  isSplitView
}) => {
  const { t } = useTranslations(translations);
  const title = t("You have applied for this job");
  return isSplitView ? /* @__PURE__ */ jsx(
    TooltipRenderer,
    {
      id: `serp_applied_${jobId}`,
      tooltip: /* @__PURE__ */ jsx(Text, { children: title }),
      children: ({ triggerProps }) => /* @__PURE__ */ jsx(
        Box,
        {
          ...triggerProps,
          data: {
            testid: "applied-job-icon",
            automation: "applied-job"
          },
          children: /* @__PURE__ */ jsx(IconPositive, { tone: "positive" })
        }
      )
    }
  ) : /* @__PURE__ */ jsxs(
    Box,
    {
      data: {
        testid: "applied-job-notice",
        automation: "applied-job"
      },
      children: [
        /* @__PURE__ */ jsx(Notice, { tone: "positive", children: /* @__PURE__ */ jsx(Text, { weight: "medium", children: /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: t("Applied") }) }) }),
        /* @__PURE__ */ jsx(HiddenVisually, { children: title })
      ]
    }
  );
};
const SaveJobButton = ({
  isSaved,
  isSplitView,
  jobId,
  toggleSavedJob
}) => {
  const { t } = useTranslations(translations);
  const buttonTitle = isSaved ? t("Remove from your saved jobs") : t("Save job");
  const buttonText = isSaved ? t("Unsave") : t("Save");
  return isSplitView ? /* @__PURE__ */ jsx(
    ButtonIcon,
    {
      id: `serp-save-job-${jobId}`,
      label: buttonText,
      variant: "transparent",
      data: {
        testid: isSaved ? "remove-save-job" : "save-job",
        automation: isSaved ? "remove-saved-job" : "save-job"
      },
      icon: /* @__PURE__ */ jsx(
        IconBookmark,
        {
          tone: isSaved ? "brandAccent" : void 0,
          active: isSaved
        }
      ),
      onClick: (e) => {
        e.stopPropagation();
        toggleSavedJob(e);
      }
    }
  ) : /* @__PURE__ */ jsxs(
    Box,
    {
      data: {
        testid: isSaved ? "remove-save-job" : "save-job",
        automation: isSaved ? "remove-saved-job" : "save-job"
      },
      title: buttonTitle,
      children: [
        isSaved && /* @__PURE__ */ jsx(HiddenVisually, { children: t("This is a saved job.") }),
        /* @__PURE__ */ jsxs(
          Button,
          {
            tone: "neutral",
            variant: "transparent",
            bleed: true,
            onClick: toggleSavedJob,
            children: [
              /* @__PURE__ */ jsx(Box, { component: "span", paddingRight: "xxsmall", children: /* @__PURE__ */ jsx(
                IconBookmark,
                {
                  active: isSaved,
                  tone: isSaved ? "brandAccent" : void 0
                }
              ) }),
              buttonText
            ]
          }
        )
      ]
    }
  );
};
const SaveJobForSignedIn = ({
  isApplied,
  isSaved,
  isSplitView,
  jobId,
  toggleSavedJob
}) => isApplied ? /* @__PURE__ */ jsx(SavedJobForApplied, { isSplitView, jobId }) : /* @__PURE__ */ jsx(
  SaveJobButton,
  {
    isSaved,
    isSplitView,
    jobId,
    toggleSavedJob
  }
);
const SaveJobForSignedOut = ({
  isSplitView,
  jobId,
  returnUrl,
  toggleSavedJob
}) => {
  const { t } = useTranslations(translations$1);
  return isSplitView ? /* @__PURE__ */ jsx(
    ButtonIcon,
    {
      id: `serp-signed-out-save-job-${jobId}`,
      label: t("Sign in to save this job"),
      variant: "transparent",
      data: {
        testid: "signed-out-save-job",
        automation: "signed-out-save-job"
      },
      icon: /* @__PURE__ */ jsx(IconBookmark, {}),
      onClick: toggleSavedJob
    }
  ) : /* @__PURE__ */ jsx(
    ButtonLink,
    {
      href: returnUrl,
      rel: "nofollow",
      "aria-label": t("Sign in to save this job"),
      data: {
        testid: "signed-out-save-job",
        automation: "signed-out-save-job"
      },
      tone: "neutral",
      variant: "transparent",
      bleed: true,
      icon: /* @__PURE__ */ jsx(IconBookmark, {}),
      onClick: toggleSavedJob,
      children: t("Save")
    }
  );
};
const SaveJob = ({
  isAuthenticated,
  isSaved,
  isApplied,
  isSplitView,
  jobId,
  returnUrl,
  toggleSavedJob
}) => isAuthenticated ? /* @__PURE__ */ jsx(
  SaveJobForSignedIn,
  {
    isApplied,
    isSaved,
    isSplitView,
    jobId,
    toggleSavedJob
  }
) : /* @__PURE__ */ jsx(
  SaveJobForSignedOut,
  {
    isSplitView,
    jobId,
    returnUrl,
    toggleSavedJob
  }
);
const SaveJob$1 = memo(SaveJob);
const ClickThroughOverlay = ({ children }) => /* @__PURE__ */ jsx(Box, { position: { desktop: "relative" }, display: "inline", children });
const premiumAdBadgeAutomation = "premiumAdBadge";
const jobListingDateAutomation = "jobListingDate";
const JobCard = ({
  "aria-label": ariaLabel,
  bulletPoints,
  companyLink,
  classificationLinks,
  id,
  isAuthenticated,
  isPremium = false,
  isSelected = false,
  isShowJobCardTeaserInSplitView = false,
  isStandOut = false,
  isSplitView,
  jobDetailPageLink,
  jobDetailPageJobCardOriginLink,
  jobDetailPageCardTitleOriginLink,
  jobLinkOnClick,
  jobMetaLinkOnClick,
  jobTitle: title,
  joraImpressionTrackingUrl,
  listingDate,
  listingDateDescription,
  locationLinks,
  locationMatch,
  srcLogo,
  salaryDisplayLabel,
  shouldAddHoverEffect = false,
  shouldAddFocusEffect = false,
  shouldOpenApplyInNewTab,
  saveJob: { isApplied, isSaved, returnUrl, toggleSavedJob },
  onClick,
  teaser,
  workType
}) => {
  const { t } = useTranslations(translations$2);
  const shouldRenderLogo = srcLogo !== void 0;
  const shouldOpenInNewTab = shouldOpenApplyInNewTab && isSplitView;
  const isMobile = useResponsiveValue()({
    mobile: true,
    desktop: false
  });
  const showJobCardTeaser = !isSplitView || isSplitView && (isShowJobCardTeaserInSplitView || isMobile);
  const premiumBadgeText = t("Featured");
  const premiumBadgeDescriptionText = t("This is a featured job");
  const overlaySaveJob = /* @__PURE__ */ jsx(ClickThroughOverlay, { children: /* @__PURE__ */ jsx(
    SaveJob$1,
    {
      isApplied,
      isSaved,
      returnUrl,
      toggleSavedJob,
      isAuthenticated,
      isSplitView,
      jobId: String(id)
    }
  ) });
  return /* @__PURE__ */ jsxs(
    Box,
    {
      component: "article",
      cursor: isSplitView ? "pointer" : "default",
      className: isSplitView ? [
        splitView.jobCardBase,
        {
          [splitView.selectedJobCard]: isSelected,
          [splitView.hoveredJobCard]: shouldAddHoverEffect,
          [splitView.focusedJobCard]: shouldAddFocusEffect
        }
      ] : [
        root,
        {
          [root_isStandOut]: !isPremium && isStandOut,
          [root_isPremium]: isPremium
        }
      ],
      padding: "gutter",
      background: isSplitView ? "surface" : void 0,
      boxShadow: !isSplitView ? "borderNeutralLight" : void 0,
      borderRadius: "large",
      "aria-label": ariaLabel,
      "aria-selected": isSelected,
      data: {
        automation: getJobDataAutomation(isPremium, locationMatch),
        testid: "job-card",
        "job-id": id,
        "card-type": "JobCard"
      },
      position: "relative",
      onClick,
      children: [
        /* @__PURE__ */ jsx(Hidden, { above: "tablet", children: /* @__PURE__ */ jsx(
          Link,
          {
            href: jobDetailPageLink.href || "",
            title: jobDetailPageLink.title,
            rel: jobDetailPageLink.rel,
            className: tappableJob,
            data: {
              automation: "job-list-view-job-link"
            },
            target: shouldOpenInNewTab ? "_blank" : "_self",
            onClick: jobLinkOnClick
          }
        ) }),
        isSplitView && /* @__PURE__ */ jsx(Hidden, { below: "desktop", children: /* @__PURE__ */ jsx(
          Link,
          {
            href: jobDetailPageJobCardOriginLink.href || "",
            title: jobDetailPageJobCardOriginLink.title,
            rel: jobDetailPageJobCardOriginLink.rel,
            className: [tappableJob, outlinedJob],
            data: {
              automation: "job-list-item-link-overlay",
              testid: "job-list-item-link-overlay",
              "run-click-only": true
            },
            onClick: jobLinkOnClick
          }
        ) }),
        joraImpressionTrackingUrl && /* @__PURE__ */ jsx(
          Box,
          {
            component: "img",
            src: joraImpressionTrackingUrl,
            className: trackingPixel,
            data: {
              testid: "jora-impression-tracking"
            }
          }
        ),
        isPremium ? /* @__PURE__ */ jsx(HiddenVisually, { children: premiumBadgeDescriptionText }) : listingDateDescription && /* @__PURE__ */ jsx(HiddenVisually, { children: listingDateDescription }),
        /* @__PURE__ */ jsxs(Stack, { space: !isSplitView && !shouldRenderLogo ? "large" : "small", children: [
          /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
            /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
              /* @__PURE__ */ jsx(Column, { children: isSplitView ? /* @__PURE__ */ jsxs(Stack, { space: shouldRenderLogo ? "xsmall" : "medium", children: [
                (isPremium || shouldRenderLogo) && /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
                  isPremium && /* @__PURE__ */ jsx(
                    Box,
                    {
                      position: "relative",
                      className: splitView.featuredBadge,
                      children: /* @__PURE__ */ jsx(
                        Badge,
                        {
                          tone: "promote",
                          bleedY: true,
                          data: { automation: premiumAdBadgeAutomation },
                          children: premiumBadgeText
                        }
                      )
                    }
                  ),
                  shouldRenderLogo && /* @__PURE__ */ jsx(LazyLoadedImg, { src: srcLogo })
                ] }),
                /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
                  /* @__PURE__ */ jsx(Text, { weight: "strong", component: "h3", children: /* @__PURE__ */ jsx(ClickThroughOverlay, { children: /* @__PURE__ */ jsx(
                    Link,
                    {
                      href: jobDetailPageCardTitleOriginLink.href || "",
                      title: jobDetailPageCardTitleOriginLink.title,
                      rel: jobDetailPageCardTitleOriginLink.rel,
                      className: {
                        [showVisited]: !isSelected,
                        [showHovered]: shouldAddHoverEffect
                      },
                      data: {
                        automation: "jobTitle",
                        "allow-propagation": !isSplitView,
                        "run-click-only": true
                      },
                      id: generateJobTitleLinkId(id),
                      onClick: jobLinkOnClick,
                      children: title
                    }
                  ) }) }),
                  /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsxs(ClickThroughOverlay, { children: [
                    /* @__PURE__ */ jsxs(HiddenVisually, { children: [
                      t("at"),
                      " "
                    ] }),
                    /* @__PURE__ */ jsx(
                      MetaLink,
                      {
                        ...companyLink,
                        type: "company",
                        isSplitView,
                        onClick: jobMetaLinkOnClick
                      }
                    )
                  ] }) })
                ] })
              ] }) : /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
                /* @__PURE__ */ jsx(Heading, { level: "4", component: "h3", children: /* @__PURE__ */ jsx(
                  TextLink,
                  {
                    href: jobDetailPageLink.href || "",
                    title: jobDetailPageLink.title,
                    rel: jobDetailPageLink.rel,
                    showVisited: true,
                    id: generateJobTitleLinkId(id),
                    onClick: jobDetailPageLink.onBeforeNavigation,
                    data: {
                      automation: "jobTitle"
                    },
                    children: title
                  }
                ) }),
                /* @__PURE__ */ jsxs(Text, { children: [
                  /* @__PURE__ */ jsxs(HiddenVisually, { children: [
                    t("at"),
                    " "
                  ] }),
                  /* @__PURE__ */ jsx(
                    MetaLink,
                    {
                      ...companyLink,
                      type: "company",
                      isSplitView,
                      onClick: jobMetaLinkOnClick
                    }
                  )
                ] })
              ] }) }),
              /* @__PURE__ */ jsx(Column, { width: "content", children: isSplitView ? overlaySaveJob : /* @__PURE__ */ jsx(Box, { "aria-hidden": "true", children: isPremium ? /* @__PURE__ */ jsx(
                Badge,
                {
                  tone: "promote",
                  bleedY: true,
                  data: { automation: premiumAdBadgeAutomation },
                  children: premiumBadgeText
                }
              ) : /* @__PURE__ */ jsx(
                Text,
                {
                  size: "small",
                  tone: "secondary",
                  data: { automation: jobListingDateAutomation },
                  children: listingDate
                }
              ) }) })
            ] }),
            /* @__PURE__ */ jsxs(Box, { children: [
              /* @__PURE__ */ jsx(HiddenVisually, { children: /* @__PURE__ */ jsx(Box, { component: "p", children: t("This is a {workType} job", { workType }) }) }),
              /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
                /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Text, { size: "small", children: locationLinks == null ? void 0 : locationLinks.map((props, index) => (
                  // the locations will be split by comma and space
                  // e.g. "Lewisham, Sydney NSW"
                  /* @__PURE__ */ jsxs(Box, { component: "span", children: [
                    index % 2 !== 0 && ", ",
                    /* @__PURE__ */ jsx(
                      MetaLink,
                      {
                        ...props,
                        type: "location",
                        isSplitView: true,
                        disabledLink: true,
                        onClick: jobMetaLinkOnClick
                      }
                    )
                  ] }, index)
                )) }) }),
                salaryDisplayLabel ? /* @__PURE__ */ jsx(Box, { "aria-label": `Salary: ${salaryDisplayLabel}`, children: /* @__PURE__ */ jsx(
                  Text,
                  {
                    size: "small",
                    data: { automation: "jobSalary" },
                    maxLines: 2,
                    children: salaryDisplayLabel
                  }
                ) }) : null,
                /* @__PURE__ */ jsx(Hidden, { below: "tablet", children: /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(Inline, { space: "none", children: classificationLinks[1] ? /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsx(HiddenVisually, { children: `subClassification: ${classificationLinks[1].name}` }),
                  /* @__PURE__ */ jsx(
                    MetaLink,
                    {
                      ...classificationLinks[1],
                      type: "subClassification",
                      isSplitView: true,
                      disabledLink: true,
                      onClick: jobMetaLinkOnClick
                    }
                  ),
                  /* @__PURE__ */ jsx(Box, { marginLeft: "xxsmall" }),
                  /* @__PURE__ */ jsx(HiddenVisually, { children: `classification: ${classificationLinks[0].name}` }),
                  /* @__PURE__ */ jsx(
                    MetaLink,
                    {
                      ...classificationLinks[0],
                      type: "classification",
                      isChild: true,
                      isSplitView: true,
                      disabledLink: true,
                      onClick: jobMetaLinkOnClick
                    }
                  )
                ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsx(HiddenVisually, { children: `classification: ${classificationLinks[0].name}` }),
                  /* @__PURE__ */ jsx(
                    MetaLink,
                    {
                      ...classificationLinks[0],
                      type: "classification",
                      isSplitView: true,
                      disabledLink: true,
                      onClick: jobMetaLinkOnClick
                    }
                  )
                ] }) }) }) })
              ] })
            ] }),
            (bulletPoints == null ? void 0 : bulletPoints.length) ? /* @__PURE__ */ jsx(List, { size: "small", space: "xsmall", children: bulletPoints.map((bulletPoint, i) => /* @__PURE__ */ jsx(Text, { size: "small", children: bulletPoint }, i)) }) : null,
            showJobCardTeaser ? /* @__PURE__ */ jsx(
              Text,
              {
                size: "small",
                tone: "secondary",
                data: {
                  testid: "job-card-teaser",
                  automation: "jobShortDescription"
                },
                children: highlightFromHTML({ html: teaser, isDescription: true })
              }
            ) : null,
            isSplitView && !isPremium && listingDate ? /* @__PURE__ */ jsx(
              Text,
              {
                size: "small",
                tone: "secondary",
                data: { automation: jobListingDateAutomation },
                children: listingDate
              }
            ) : null
          ] }),
          !isSplitView && /* @__PURE__ */ jsxs(Columns, { space: "none", alignY: "bottom", children: [
            /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Inline, { space: "none", children: overlaySaveJob }) }),
            shouldRenderLogo ? /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Bleed, { bottom: "xsmall", children: /* @__PURE__ */ jsx(LazyLoadedImg, { src: srcLogo }) }) }) : null
          ] })
        ] })
      ]
    }
  );
};
export {
  JobCard
};
